import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Alert, Button, Checkbox, Input } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { applyFieldErrors, errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { studentRegisterSchema } from '@/validations';

import { AuthCard } from './AuthCard';

export function RegisterStudentPage() {
  const { registerStudent } = useAuth();
  const navigate = useNavigate();
  // From an invite link. The server ignores a code that matches nobody.
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref')?.trim() || null;

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(studentRegisterSchema),
    defaultValues: { accepted_terms: false },
  });

  async function onSubmit(values) {
    try {
      const session = await registerStudent({
        ...values,
        ...(referralCode ? { referral_code: referralCode } : {}),
      });
      navigate(`${paths.verifyEmail}?email=${encodeURIComponent(session.user.email)}`, {
        state: { debugCode: session.verification_code_debug ?? null, codeSent: true },
      });
    } catch (error) {
      if (!applyFieldErrors(error, setError)) {
        setError('root', { message: errorMessage(error, 'Could not create the account') });
      }
    }
  }

  return (
    <AuthCard
      title="Create your student account"
      description="One profile for every internship application."
      footer={
        <>
          <p>
            Already have an account?{' '}
            <Link to={paths.login} className="text-brand-600 font-medium">
              Log in
            </Link>
          </p>
          <p>
            Are you a company?{' '}
            <Link to={paths.registerCompany} className="text-brand-600 font-medium">
              Sign up as Company
            </Link>
          </p>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {errors.root && <Alert tone="error">{errors.root.message}</Alert>}
        {referralCode && (
          <p className="text-brand-700 bg-brand-50 rounded-lg px-3 py-2 text-xs font-medium">
            Invited by a friend. Welcome aboard.
          </p>
        )}

        <Input
          label="Full name"
          autoComplete="name"
          required
          error={errors.full_name?.message}
          {...register('full_name')}
        />
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          required
          error={errors.email?.message}
          {...register('email')}
        />
        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          required
          hint="At least 8 characters, with upper and lower case and a number."
          error={errors.password?.message}
          {...register('password')}
        />

        <Checkbox
          label={
            <>
              I accept the <span className="underline">terms</span> and{' '}
              <span className="underline">privacy policy</span>
            </>
          }
          error={errors.accepted_terms?.message}
          {...register('accepted_terms')}
        />

        <Button type="submit" fullWidth isLoading={isSubmitting}>
          Sign up as Student
        </Button>
      </form>
    </AuthCard>
  );
}
